import { api } from "@/api/api";
import type { OnError, OnSuccess } from "@/api/types";
import {
  getResponseStatusCode,
  isAbortError,
  notifyErrorNotAuthRelated,
} from "@/api/utils";
import {
  check as checkRequest,
  login as loginRequest,
} from "@/api/webapi/Auth";
import { log, SEVERITY } from "@/log/log.ts";
import type { RightString } from "@/models/api/Right.ts";
import type AuthCheckModel from "@/models/AuthCheck";
import * as Sentry from "@sentry/vue";
import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, readonly, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { SECONDS } from "@/utils/milliseconds.ts";

export enum AuthSessionState {
  unknown = "unknown",
  checking = "checking",
  authenticated = "authenticated",
  unauthenticated = "unauthenticated",
  error = "error",
}

export const useAuthStore = defineStore("authStore", () => {
  const router = useRouter();
  const currentUser = ref<AuthCheckModel | null>(null);
  const currentUserReadOnly = readonly(currentUser);
  const token = useLocalStorage<null | string>("token", null);

  const sessionState = ref(
    token.value == null
      ? AuthSessionState.unauthenticated
      : AuthSessionState.unknown,
  );

  const isLoggedIn = computed(
    () => sessionState.value === AuthSessionState.authenticated,
  );
  const isLoginLoading = computed(
    () =>
      sessionState.value === AuthSessionState.unknown ||
      sessionState.value === AuthSessionState.checking,
  );
  const isLoggedOut = computed(
    () => sessionState.value === AuthSessionState.unauthenticated,
  );
  const isSessionReady = computed(
    () =>
      sessionState.value !== AuthSessionState.unknown &&
      sessionState.value !== AuthSessionState.checking,
  );

  let sessionGeneration = 0;
  let activeCheck: {
    promise: Promise<boolean>;
    controller: AbortController;
    cancel: () => void;
  } | null = null;

  function invalidateSessionRequests() {
    sessionGeneration++;
    const previous = activeCheck;
    activeCheck = null;
    previous?.cancel();
    previous?.controller.abort();
  }

  function setLoggedOut() {
    invalidateSessionRequests();
    sessionState.value = AuthSessionState.unauthenticated;
    token.value = null;
    currentUser.value = null;
    api.unauthorize();
  }

  function setLoginPending() {
    sessionState.value = AuthSessionState.checking;
    currentUser.value = null;
  }

  function setLoggedIn(user: AuthCheckModel) {
    sessionState.value = AuthSessionState.authenticated;
    currentUser.value = user;
  }

  /** Go to login page on logout */
  watch(sessionState, async (is, was) => {
    if (
      was === AuthSessionState.authenticated &&
      is === AuthSessionState.unauthenticated
    ) {
      await router.push("/login");
    }
  });

  watch(
    token,
    () => {
      invalidateSessionRequests();
      sessionState.value =
        token.value == null
          ? AuthSessionState.unauthenticated
          : AuthSessionState.unknown;
      currentUser.value = null;
      if (token.value == null) api.unauthorize();
      else api.authorize(token.value);
    },
    { flush: "sync" },
  );

  const tokenExpirationDate = computed<Date | null>(() => {
    let date: null | Date = null;
    if (token.value != null) {
      const payload = parseJWTPayload(token.value);
      if ("exp" in payload) {
        date = new Date((payload.exp as number) * SECONDS); // seconds to milliseconds
      }
    }
    return date;
  });

  /** reject outdated tokens */
  watch(tokenExpirationDate, checkTokenDate, { immediate: true });

  function checkTokenDate(tokenExpirationDate: Date | null) {
    if (
      tokenExpirationDate != null &&
      tokenExpirationDate.getTime() < Date.now()
    ) {
      setLoggedOut();
    }
    //Todo: try to get a new token if time is between 0-4am
  }

  function parseJWTPayload(token: string): object {
    const parts = token.split(".");
    if (parts.length !== 3) {
      //TODO log.error('JWT Parse Error: Token has unexpected shape <' + token + '>')
      return {};
    }
    try {
      return JSON.parse(window.atob(parts[1])) as object;
    } catch {
      // log.error('JWT Parse Error:', e, '; with token <' + token + '>')
      return {};
    }
  }

  function setToken(_token: string): void {
    token.value = _token;
    api.authorize(_token);
  }

  function getToken() {
    return token.value;
  }

  /** Bootstrap an unconfirmed session, or refresh without unmounting authenticated content. */
  async function updateSession(): Promise<boolean> {
    checkTokenDate(tokenExpirationDate.value);
    if (token.value == null) {
      setLoggedOut();
      return false;
    }
    if (activeCheck) return activeCheck.promise;

    const generation = sessionGeneration;
    const confirmed = isLoggedIn.value;
    if (!confirmed) setLoginPending();
    const controller = new AbortController();
    let cancel!: () => void;
    const cancelled = new Promise<boolean>((resolve) => {
      cancel = () => resolve(false);
    });
    const request = (async () => {
      try {
        const user = await checkRequest(controller.signal);
        if (generation !== sessionGeneration) return false;
        setLoggedIn(user);
        updateSentryUser(user);
        return true;
      } catch (error) {
        if (generation !== sessionGeneration) return false;
        if (!isAbortError(error)) {
          log.addError(error, { severity: SEVERITY.WARNING });
        }
        const status = getResponseStatusCode(error);
        if (status === 401 || status === 403) {
          setLoggedOut();
        } else if (!confirmed) {
          sessionState.value = AuthSessionState.error;
          currentUser.value = null;
        }
        return isLoggedIn.value;
      }
    })();
    const promise = Promise.race([request, cancelled]);
    const check = { promise, controller, cancel };
    activeCheck = check;
    try {
      return await promise;
    } finally {
      if (activeCheck === check) activeCheck = null;
    }
  }

  async function login(
    username: string,
    password: string,
    options: {
      onSuccess?: OnSuccess;
      onError?: OnError;
    } = {},
  ): Promise<void> {
    setLoggedOut();
    let generation = sessionGeneration;
    setLoginPending();
    await loginRequest(username, password)
      .then((fetchedToken) => {
        if (generation !== sessionGeneration) return;
        token.value = fetchedToken;
        generation = sessionGeneration;
        setLoginPending();
        api.authorize(fetchedToken);
        return checkRequest();
      })
      .then((currentUser) => {
        if (generation !== sessionGeneration || !currentUser) return;
        setLoggedIn(currentUser);
        updateSentryUser(currentUser);
        if (options.onSuccess) options.onSuccess();
      })
      .catch((error: unknown) => {
        if (generation !== sessionGeneration) return;
        setLoggedOut();
        if (options.onError) options.onError(error);
      });
  }

  /** updates user rights once per minute */
  function runPeriodicUpdates() {
    setTimeout(() => {
      if (isLoggedIn.value) {
        updateSession().catch(notifyErrorNotAuthRelated);
      }
      runPeriodicUpdates();
    }, 60_000);
  }

  // Initialize Store
  if (import.meta.env.PROD) {
    runPeriodicUpdates();
  }

  function updateSentryUser(user: AuthCheckModel) {
    Sentry.setUser({
      ...user,
      id: user.userId,
      userId: undefined,
    });
  }

  return {
    logout: setLoggedOut,
    login,
    setToken,
    getToken,
    updateSession,
    isLoggedIn,
    isLoggedOut,
    isLoginLoading,
    isSessionReady,
    sessionState: readonly(sessionState),
    currentUser: currentUserReadOnly,
  };
});

export function useUserRight(right: RightString) {
  const authStore = useAuthStore();
  const hasRight = ref(false);
  authStore.$subscribe(
    (_mutation, state) => {
      hasRight.value =
        (state.currentUser?.isAdmin ||
          state.currentUser?.userRights.includes(right)) ??
        false;
    },
    { immediate: true, deep: true },
  );

  return { hasRight };
}

export function useUseRightAnyOf(rights: RightString[]) {
  const authStore = useAuthStore();
  const hasRight = ref(false);
  authStore.$subscribe(
    (_mutation, state) => {
      hasRight.value =
        (state.currentUser?.isAdmin ||
          state.currentUser?.userRights.some((x) => rights.includes(x))) ??
        false;
    },
    { immediate: true, deep: true },
  );

  return { hasRight };
}

export function hasRight(right: RightString): boolean {
  const authStore = useAuthStore();
  return (
    (authStore.currentUser?.isAdmin ||
      authStore.currentUser?.userRights.includes(right)) ??
    false
  );
}
